/**
 * Newly-earned-badge toast (GAM-US-3).
 *
 * Mounted at the App layout level so it can announce a badge earned via
 * any qualifying event (lesson complete, module quiz pass, exam pass,
 * cert issuance) without each event surface needing a hook.
 *
 * On mount AND on each route change, fetches the badge shelf and looks
 * for ``earned && !seen`` rows. Renders a single dismissable toast
 * grouping all unseen badges; on dismiss (or after the auto-hide
 * timeout), marks them seen so the toast doesn't reappear on the next
 * navigation.
 *
 * The server-side ``seen_at`` flag is the source of truth — even if
 * the auto-mark POST fails the toast won't double-display because the
 * useEffect's ``shown`` guard prevents same-mount re-render, AND the
 * next page load's getBadges call won't see the rows as unseen if the
 * earlier mark eventually committed.
 *
 * Accessibility: ``role="status"`` + ``aria-live="polite"`` so a
 * screen reader announces the badge without yanking focus. The dismiss
 * button has an explicit ``aria-label``.
 */
import type * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { ApiError } from "@/lib/api";
import {
  type BadgeStatus,
  getBadges,
  markBadgesSeen,
} from "@/lib/gamification-api";

interface BadgeToastFetchers {
  badges: typeof getBadges;
  markSeen: typeof markBadgesSeen;
}

const DEFAULTS: BadgeToastFetchers = {
  badges: getBadges,
  markSeen: markBadgesSeen,
};

export interface BadgeToastProps {
  /** Test seam. */
  fetchers?: Partial<BadgeToastFetchers>;
  /** Auto-dismiss timeout in ms (default 6000). 0 disables auto-dismiss. */
  autoHideMs?: number;
}

export function BadgeToast({
  fetchers: fetchersOverride = {},
  autoHideMs = 6000,
}: BadgeToastProps = {}): React.JSX.Element | null {
  const fetchers = useMemo<BadgeToastFetchers>(
    () => ({ ...DEFAULTS, ...fetchersOverride }),
    [fetchersOverride],
  );
  const location = useLocation();
  const [unseen, setUnseen] = useState<BadgeStatus[]>([]);

  const dismiss = useCallback(
    (slugs: string[]): void => {
      setUnseen([]);
      if (slugs.length === 0) return;
      // Best-effort. Silently swallow failures — the server still has
      // the row marked NULL and the next page load will surface it again
      // (preferred over silently losing the badge announcement on a
      // transient network hiccup).
      void fetchers.markSeen(slugs).catch(() => {
        /* tolerated */
      });
    },
    [fetchers],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await fetchers.badges();
        if (cancelled) return;
        // Defensive: backend may return `{}` or omit badges for users
        // who aren't enrolled / can't see learner endpoints. Treat
        // missing as empty so this never throws and tears down the
        // host page (the toast is decorative, not load-bearing).
        const fresh = (resp?.badges ?? []).filter((b) => b.earned && !b.seen);
        if (fresh.length > 0) {
          setUnseen(fresh);
        }
      } catch (err) {
        // 401 / 403 mean the learner can't see the toast anyway (not
        // logged in, no enrollment) — drop silently.
        if (err instanceof ApiError) return;
        // Any other error: log to console for debugging, no UI surface.
        console.warn("BadgeToast: failed to fetch badges", err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Re-run on route change so a lesson-complete navigation refreshes
    // the toast state immediately.
  }, [location.pathname, fetchers]);

  useEffect(() => {
    if (unseen.length === 0 || autoHideMs <= 0) return undefined;
    const slugs = unseen.map((b) => b.slug);
    const t = window.setTimeout(() => {
      dismiss(slugs);
    }, autoHideMs);
    return () => {
      window.clearTimeout(t);
    };
  }, [unseen, autoHideMs, dismiss]);

  if (unseen.length === 0) return null;

  const slugs = unseen.map((b) => b.slug);
  const lead =
    unseen.length === 1
      ? `You earned a badge: ${unseen[0]!.title}`
      : `You earned ${unseen.length} badges`;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-auto fixed bottom-6 right-6 z-50 max-w-sm rounded-lg border border-amber-300 bg-amber-50 p-4 shadow-lg"
    >
      <div className="flex items-start gap-3">
        <div
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-200 text-lg font-semibold text-amber-900"
        >
          ★
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-amber-900">{lead}</p>
          {unseen.length > 1 ? (
            <ul className="mt-1 list-disc pl-4 text-xs text-amber-900">
              {unseen.map((b) => (
                <li key={b.slug}>{b.title}</li>
              ))}
            </ul>
          ) : (
            <p className="mt-1 text-xs text-amber-900">{unseen[0]!.description}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => dismiss(slugs)}
          aria-label="Dismiss badge announcement"
          className="ml-2 rounded p-1 text-amber-900 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-600"
        >
          ×
        </button>
      </div>
    </div>
  );
}
